import { useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "../utils/QueryClient";
import {
  getNotifications,
  getNotificationsForCounts,
  getUnreadCount,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  NotificationFilterDto,
  NotificationPage,
} from "../services/NotificationService";
import { addNotificationListener } from "../utils/notificationSocket";
import { addStompConnectListener, isStompConnected } from "../utils/stompClient";
import { NotificationResponseDto } from "../models/notification/NotificationResponseDto";
import { isNotificationRead } from "../constances/NotificationsConstance";

const UNREAD_KEY = ["notifications", "unread-count"];
const LIST_KEY = ["notifications", "list"];
const COUNT_ITEMS_KEY = ["notifications", "count-items"];
export const NOTIFICATIONS_INVALIDATED_EVENT = "adc:notifications-invalidated";

const locallyReadNotificationIds = new Set<string>();
let locallyReadAllAt: number | null = null;
let activeSocketConsumers = 0;
let stopNotificationSocket: (() => void) | null = null;

const invalidateAll = () => {
  queryClient.invalidateQueries({ queryKey: UNREAD_KEY });
  queryClient.invalidateQueries({ queryKey: LIST_KEY });
  queryClient.invalidateQueries({ queryKey: COUNT_ITEMS_KEY });
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(NOTIFICATIONS_INVALIDATED_EVENT));
  }
};

const getNotificationTime = (iso?: string) => {
  const value = iso ? new Date(iso).getTime() : Number.NaN;
  return Number.isFinite(value) ? value : 0;
};

const restoreLocalReadState = (ids: Set<string>, readAllAt: number | null) => {
  locallyReadNotificationIds.clear();
  ids.forEach((id) => locallyReadNotificationIds.add(id));
  locallyReadAllAt = readAllAt;
};

const rememberCachedNotificationsAsRead = () => {
  queryClient.getQueriesData<NotificationPage>({ queryKey: LIST_KEY }).forEach(([, page]) => {
    page?.content.forEach((notification) => locallyReadNotificationIds.add(notification.id));
  });
};

const normalizeFilter = (filter?: NotificationFilterDto): NotificationFilterDto => ({
  group: filter?.group || undefined,
  keyword: filter?.keyword?.trim() || undefined,
  isRead: filter?.isRead,
  fromDate: filter?.fromDate || undefined,
  toDate: filter?.toDate || undefined,
});

const shouldDisplayAsRead = (notification: NotificationResponseDto) =>
  isNotificationRead(notification) ||
  locallyReadNotificationIds.has(notification.id) ||
  (locallyReadAllAt !== null && getNotificationTime(notification.registeredTime) <= locallyReadAllAt);

const applyClientReadStateToNotification = (notification: NotificationResponseDto): NotificationResponseDto => ({
  ...notification,
  isRead: shouldDisplayAsRead(notification),
});

const applyClientReadState = (page: NotificationPage): NotificationPage => ({
  ...page,
  content: page.content.map(applyClientReadStateToNotification),
});

const getCachedNotificationIds = () => {
  const ids = new Set<string>();
  queryClient.getQueriesData<NotificationPage>({ queryKey: LIST_KEY }).forEach(([, page]) => {
    page?.content.forEach((notification) => ids.add(notification.id));
  });
  return ids;
};

const upsertPushedNotification = (notification: NotificationResponseDto) => {
  const normalized = applyClientReadStateToNotification(notification);
  const cachedIds = getCachedNotificationIds();
  const alreadyCached = cachedIds.has(normalized.id);
  const shouldCountUnread = !alreadyCached && !isNotificationRead(normalized);

  queryClient.setQueryData<number>(UNREAD_KEY, (old) => {
    if (!shouldCountUnread) return old;
    return typeof old === "number" ? old + 1 : old;
  });

  queryClient.setQueriesData<NotificationResponseDto[]>({ queryKey: COUNT_ITEMS_KEY }, (old) =>
    old ? [normalized, ...old.filter((item) => item.id !== normalized.id)] : old,
  );
};

const setCachedNotificationsRead = (readResolver: (notification: NotificationResponseDto) => boolean) => {
  queryClient.setQueriesData<NotificationPage>({ queryKey: LIST_KEY }, (old) => {
    if (!old) return old;

    return applyClientReadState({
      ...old,
      content: old.content.map((notification) => ({
        ...notification,
        isRead: readResolver(notification),
      })),
    });
  });

  queryClient.setQueriesData<NotificationResponseDto[]>({ queryKey: COUNT_ITEMS_KEY }, (old) =>
    old?.map((notification) => ({
      ...notification,
      isRead: readResolver(notification),
    })),
  );
};

export const useUnreadCount = (enabled = true) =>
  useQuery({ queryKey: UNREAD_KEY, queryFn: getUnreadCount, enabled, staleTime: 30_000 });

export const useNotifications = (page = 0, size = 10, enabled = true, filter?: NotificationFilterDto) => {
  const normalizedFilter = normalizeFilter(filter);
  return useQuery({
    queryKey: [...LIST_KEY, page, size, normalizedFilter],
    queryFn: () => getNotifications(page, size, normalizedFilter),
    enabled,
    staleTime: 15_000,
    select: applyClientReadState,
  });
};

export const useNotificationCountItems = (enabled = true, filter?: NotificationFilterDto) => {
  const normalizedFilter = normalizeFilter(filter);
  return useQuery({
    queryKey: [...COUNT_ITEMS_KEY, normalizedFilter],
    queryFn: () => getNotificationsForCounts(100, normalizedFilter),
    enabled,
    staleTime: 30_000,
    select: (items) => items.map(applyClientReadStateToNotification),
  });
};

export const useMarkRead = () =>
  useMutation({
    mutationFn: (id: string) => markNotificationAsRead(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: UNREAD_KEY });
      await queryClient.cancelQueries({ queryKey: LIST_KEY });

      const previousUnreadCount = queryClient.getQueryData<number>(UNREAD_KEY);
      const previousLists = queryClient.getQueriesData<NotificationPage>({ queryKey: LIST_KEY });
      const previousCountItems = queryClient.getQueryData<NotificationResponseDto[]>(COUNT_ITEMS_KEY);
      const previousLocalReadIds = new Set(locallyReadNotificationIds);
      const previousReadAllAt = locallyReadAllAt;

      locallyReadNotificationIds.add(id);
      queryClient.setQueryData<number>(UNREAD_KEY, (old) => (typeof old === "number" ? Math.max(old - 1, 0) : old));
      setCachedNotificationsRead((notification) => (notification.id === id ? true : isNotificationRead(notification)));

      return { previousUnreadCount, previousLists, previousCountItems, previousLocalReadIds, previousReadAllAt };
    },
    onError: (_error, _id, context) => {
      if (context) {
        restoreLocalReadState(context.previousLocalReadIds, context.previousReadAllAt);
      }
      queryClient.setQueryData(UNREAD_KEY, context?.previousUnreadCount);
      queryClient.setQueryData(COUNT_ITEMS_KEY, context?.previousCountItems);
      context?.previousLists.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
    },
    onSettled: invalidateAll,
  });

export const useMarkAllRead = () =>
  useMutation({
    mutationFn: () => markAllNotificationsAsRead(),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: UNREAD_KEY });
      await queryClient.cancelQueries({ queryKey: LIST_KEY });

      const previousUnreadCount = queryClient.getQueryData<number>(UNREAD_KEY);
      const previousLists = queryClient.getQueriesData<NotificationPage>({ queryKey: LIST_KEY });
      const previousCountItems = queryClient.getQueryData<NotificationResponseDto[]>(COUNT_ITEMS_KEY);
      const previousLocalReadIds = new Set(locallyReadNotificationIds);
      const previousReadAllAt = locallyReadAllAt;

      rememberCachedNotificationsAsRead();
      locallyReadAllAt = Date.now();
      queryClient.setQueryData<number>(UNREAD_KEY, 0);
      setCachedNotificationsRead(() => true);

      return { previousUnreadCount, previousLists, previousCountItems, previousLocalReadIds, previousReadAllAt };
    },
    onError: (_error, _variables, context) => {
      if (context) {
        restoreLocalReadState(context.previousLocalReadIds, context.previousReadAllAt);
      }
      queryClient.setQueryData(UNREAD_KEY, context?.previousUnreadCount);
      queryClient.setQueryData(COUNT_ITEMS_KEY, context?.previousCountItems);
      context?.previousLists.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
    },
    onSettled: invalidateAll,
  });

const startNotificationSocket = () => {
  activeSocketConsumers += 1;

  if (stopNotificationSocket) {
    return () => {
      activeSocketConsumers = Math.max(activeSocketConsumers - 1, 0);
      if (activeSocketConsumers === 0 && stopNotificationSocket) {
        stopNotificationSocket();
        stopNotificationSocket = null;
      }
    };
  }

  const unsubscribe = addNotificationListener((n: NotificationResponseDto) => {
    upsertPushedNotification(n);
    invalidateAll();
    // antdNotification.open({
    //   message: n.title || "Thông báo mới",
    //   description: n.actorName ? `${n.actorName}: ${n.content ?? ""}` : n.content,
    //   placement: "bottomRight",
    // });
  });

  let skipNextConnect = !isStompConnected();
  const offConnect = addStompConnectListener(() => {
    if (skipNextConnect) {
      skipNextConnect = false;
      invalidateAll();
      return;
    }
    invalidateAll();
  });

  stopNotificationSocket = () => {
    unsubscribe();
    offConnect();
  };

  return () => {
    activeSocketConsumers = Math.max(activeSocketConsumers - 1, 0);
    if (activeSocketConsumers === 0 && stopNotificationSocket) {
      stopNotificationSocket();
      stopNotificationSocket = null;
    }
  };
};

// Mở WebSocket khi đã đăng nhập; nhận push -> cập nhật cache ngay + refetch để bù gap reconnect.
export const useNotificationSocket = (enabled: boolean) => {
  useEffect(() => {
    if (!enabled) return;
    return startNotificationSocket();
  }, [enabled]);
};
